import { useRef } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

const AREAS = [
  {
    city: 'Las Cruces',
    state: 'NM',
    services: ['Landscape Design in Las Cruces', 'Hardscaping in Las Cruces', 'Outdoor Living Construction'],
    img: 'https://public.readdy.ai/ai/img_res/00564c81a9ba8aa3a9f4ac16cd69e981.jpg',
  },
  {
    city: 'Alamogordo',
    state: 'NM',
    services: ['Landscape Design in Alamogordo', 'Hardscaping in Alamogordo', 'Irrigation Systems'],
    img: 'https://readdy.ai/api/search-image?query=upscale%20residential%20property%20Alamogordo%20New%20Mexico%20professional%20landscaping%20stone%20walkways%20warm%20desert%20sunlight%20premium%20outdoor%20spaces&width=600&height=400&seq=area-alo&orientation=landscape',
  },
  {
    city: 'El Paso',
    state: 'TX',
    services: ['Landscape Design in El Paso', 'Outdoor Living Spaces', 'Lighting Systems in El Paso'],
    img: 'https://readdy.ai/api/search-image?query=luxury%20home%20beautiful%20desert%20landscaping%20El%20Paso%20Texas%20elegant%20stone%20patio%20drought%20resistant%20plants%20architectural%20lighting%20warm%20evening%20premium%20residential&width=600&height=400&seq=area-ep&orientation=landscape',
  },
  {
    city: 'Ruidoso',
    state: 'NM',
    services: ['Landscape Design in Ruidoso', 'Hardscaping in Ruidoso', 'Lawn Installation in Ruidoso'],
    img: 'https://readdy.ai/api/search-image?query=mountain%20residential%20property%20Ruidoso%20New%20Mexico%20lush%20green%20landscaping%20pine%20trees%20elegant%20stone%20patio%20mountain%20cabin%20high%20end&width=600&height=400&seq=area-rid&orientation=landscape',
  },
  {
    city: 'Roswell',
    state: 'NM',
    services: ['Landscape Design in Roswell', 'Hardscaping in Roswell', 'Full Property Transformations'],
    img: 'https://readdy.ai/api/search-image?query=elegant%20residential%20property%20Roswell%20New%20Mexico%20professional%20landscape%20design%20green%20lawn%20stone%20walkways%20ornamental%20plants%20southwestern%20daylight&width=600&height=400&seq=area-ros&orientation=landscape',
  },
  {
    city: 'White Sands',
    state: 'NM',
    services: ['Landscape Design near White Sands', 'Hardscaping', 'Irrigation near White Sands'],
    img: 'https://readdy.ai/api/search-image?query=luxury%20desert%20landscape%20design%20southern%20New%20Mexico%20xeriscaping%20native%20plants%20stone%20features%20dramatic%20desert%20sky%20high%20end%20residential&width=600&height=400&seq=area-ws&orientation=landscape',
  },
];

export default function ServiceAreas() {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.1 });

  return (
    <section id="service-areas" className="bg-charcoal-900 py-28">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="font-sans text-stone-500 text-xs tracking-[0.25em] uppercase mb-4">Where We Work</p>
          <h2 className="font-serif text-white font-semibold leading-tight mb-5" style={{ fontSize: 'clamp(32px, 4vw, 52px)' }}>
            Serving Local Homeowners with<br />
            <em className="italic text-stone-400">Premium Landscaping</em>
          </h2>
          <p className="font-sans text-stone-400 text-base max-w-xl mx-auto leading-relaxed">
            We proudly serve homeowners across southern New Mexico and the El Paso region with the same premium craftsmanship on every project.
          </p>
        </div>

        {/* Google Maps embed */}
        <div
          className={`w-full h-80 rounded-lg overflow-hidden mb-12 border border-white/10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        >
          <iframe
            src="https://maps.google.com/maps?q=Brainard%27s+Greenscapes+Las+Cruces+NM&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0, filter: 'grayscale(20%)' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Brainard's Greenscapes Service Area Map - Las Cruces, NM"
          />
        </div>

        {/* Area cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {AREAS.map((area, i) => (
            <AreaCard key={area.city} area={area} delay={i * 80} />
          ))}
        </div>
      </div>
    </section>
  );
}

function AreaCard({ area, delay }: { area: typeof AREAS[0]; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.2 });

  return (
    <div
      ref={ref}
      className={`group relative rounded-lg overflow-hidden cursor-pointer transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-full h-44 overflow-hidden">
        <img src={area.img} alt={`Landscaping in ${area.city}, ${area.state}`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
      </div>
      <div className="bg-charcoal-800 border border-white/5 p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-map-pin-2-fill text-forest-400 text-sm" />
          </div>
          <h3 className="font-serif text-white text-base font-semibold">
            {area.city}, <span className="text-stone-400">{area.state}</span>
          </h3>
        </div>
        <ul className="space-y-1.5">
          {area.services.map((svc) => (
            <li key={svc} className="flex items-center gap-2">
              <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-forest-400 text-xs" />
              </div>
              <span className="font-sans text-stone-400 text-xs">{svc}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
